import { LinkOutlined, ShareAltOutlined } from "@ant-design/icons";
import { Button, Divider, Drawer, message, Radio, Space, Steps, Typography } from "antd";
import React, { useEffect, useState } from "react";

const { Title, Text } = Typography;

interface JobShareDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  jobTitle: string;
  jobLink: string;
  selectedPlatform?: string;
}

type Option = "1" | "2";

const JobShareDrawer: React.FC<JobShareDrawerProps> = ({
  isOpen,
  onClose,
  jobTitle,
  jobLink,
  selectedPlatform,
}) => {
  const [option, setOption] = useState<Option>("1");
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsLinkCopied(false);
      setOption("1");
    }
  }, [isOpen]);

  const getPlatformTitle = () => {
    const normalizedPlatform = selectedPlatform?.toLowerCase();
    if (normalizedPlatform === "naukri") {
      return "Share on Naukri.com";
    }
    return "Share Job Post";
  };

  const naukriSteps = {
    "1": [
      {
        title: "Log in to Naukri",
        description: "Log in to your Naukri Recruiter account.",
      },
      {
        title: "Post a Job",
        description: "Go to Post a Job.",
      },
      {
        title: "Fill job details",
        description: `Fill in:
- Job Title: ${jobTitle}
- Description
- Required Skills
- Location
- Experience`,
      },
      {
        title: "Add application link",
        description: `In the "Apply link" or "Redirect URL" field, paste your Qualify Me job link: ${jobLink}`,
      },
      {
        title: "Post",
        description: "Preview and Post.",
      },
    ],
    "2": [
      {
        title: "Log in to Naukri",
        description: "Log in to your Naukri Recruiter.",
      },
      {
        title: "Go to Resdex",
        description: "Go to Resdex (resume search).",
      },
      {
        title: "Filter candidates",
        description: "Filter for relevant candidates.",
      },
      {
        title: "Send message",
        description: `Send a custom message with the Qualify Me job link to apply:
Hi [Candidate Name],
We're hiring for ${jobTitle}. Based on your profile, we believe you could be a great fit.
Apply here: ${jobLink}`,
      },
    ],
  };

  const getSteps = () => {
    return naukriSteps[option];
  };

  const handleCopy = async () => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(jobLink);        
        message.success("Job link copied to clipboard!");
        setIsLinkCopied(true);
      } catch (err) {      
        message.error("Failed to copy job link.");
        console.error("Failed to copy: ", err);
      }
    } else {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = jobLink;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        message.success("Job link copied to clipboard!");
        setIsLinkCopied(true);
      } catch (err) {
        message.error("Failed to copy job link.");
        console.error("Fallback: Oops, unable to copy", err);
      }

      document.body.removeChild(textArea);
    }
  };

  const handleNavigateToPlatform = () => {
    window.open("https://www.naukri.com", "_blank");
  };

  return (
    <Drawer
      title={getPlatformTitle()}
      placement="right"
      onClose={onClose}
      open={isOpen}
      width={500}
    >
      <div className="job-share-content">
        <Title level={4}>{jobTitle}</Title>
        <Text type="secondary">Follow these steps to share the job post on Naukri.com:</Text>

        <div style={{ marginTop: "16px", marginBottom: "16px" }}>
          <Text strong style={{ fontSize: "16px" }}>
            Select an option:
          </Text>
          <Radio.Group
            value={option}
            onChange={(e) => setOption(e.target.value)}
            style={{ marginTop: "12px" }}
          >
            <Space direction="vertical" size="large">
              <Radio value="1" style={{ fontSize: "15px", lineHeight: "1" }}>
                Option 1: Post a Job (For Recruiters With Access)
              </Radio>
              <Radio value="2" style={{ fontSize: "15px", lineHeight: "1" }}>
                Option 2: Promote via Resume Database or Mass Mail
              </Radio>
            </Space>
          </Radio.Group>
        </div>

        <Divider />

        <Steps
          direction="vertical"
          current={0}
          items={getSteps()}
          style={{ marginTop: "16px" }}
        />

        <div
          className="share-actions"
          style={{ marginTop: "24px", marginBottom: "30px" }}
        >
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Button
              type="primary"
              icon={<ShareAltOutlined />}
              onClick={handleCopy}
            >
              Copy Job Link
            </Button>
            
            {/* Navigation button - show after link is copied */}
            {isLinkCopied && (
              <Button
                type="default"
                icon={<LinkOutlined />}
                onClick={handleNavigateToPlatform}
                style={{ 
                  background: "transparent",
                  border: "1px solid #d9d9d9",
                  color: "#1890ff"
                }}
              >
                Navigate to Naukri.com
              </Button>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default JobShareDrawer;
